import application from 'application/application';

application.directive('scrollTopButton', [
	'$interval',
	'$window',
	($interval, $window) => ({
		restrict: 'E',
		replace: true,
		template: `
			<button
				type="button"
				class="scroll-top-button"
				ng-class="{ 'scroll-top-button--visible': isVisible }"
				ng-click="scrollToTop()"
				ng-mouseenter="showTooltip()"
				ng-mouseleave="hideTooltip()"
				aria-label="Voltar ao topo"
			>
				<i class="material-icons" aria-hidden="true">keyboard_arrow_up</i>
				<span
					class="scroll-top-button__tooltip"
					ng-class="{ 'scroll-top-button__tooltip--visible': isTooltipVisible }"
				>
					Voltar ao topo
				</span>
			</button>
		`,
		link(scope, element) {
			const scrollThreshold = 300;
			const iframeSelectors = [
				'#module-financial',
				'#module-intimation',
				'#module-movements',
				'#module-people',
				'#module-schedule'
			];
			const scrollableSelectors = [
				'app-root',
				'app-root > *',
				'#contentContent',
				'#ui-view-default',
				'main',
				'app-module-financial',
				'app-categories',
				'app-cash-flow',
				'app-module-intimation',
				'app-module-movements',
				'app-all-account',
				'app-publications',
				'app-folder-publications',
				'app-processes',
				'app-folder-processes',
				'app-processes-inss',
				'app-folder-processes-inss',
				'app-dump',
				'app-details',
				'app-registered-accesses',
				'app-module-people'
			];
			const ignoredScrollTargetSelectors = [
				'.important-list',
				'.observations .card-body',
				'.observations .observation-list'
			];
			const modalSelectors = [
				'.modal.show',
				'.modal.in',
				'.modal[style*="display: block"]',
				'.modal-backdrop.show',
				'.modal-backdrop.in',
				'.mat-dialog-container',
				'.mat-mdc-dialog-container',
				'.cdk-dialog-container',
				'.md-dialog-container',
				'md-dialog',
				'[role="dialog"][aria-modal="true"]',
				'.swal2-container'
			];

			let raf = null;
			let currentTarget = null;
			let detachCurrentTarget = null;
			let checkInterval = null;
			let discoveredDocument = null;
			let discoveredCandidates = [];
			let lastDiscovery = 0;
			let suppressTooltip = false;
			const buttonElement = element[0];
			const discoveryInterval = 1500;

			document.body.appendChild(buttonElement);

			function getScrollElement(doc) {
				return doc.scrollingElement || doc.documentElement || doc.body;
			}

			function getActiveIframe() {
				for (let i = 0; i < iframeSelectors.length; i += 1) {
					const iframe = document.querySelector(iframeSelectors[i]);

					if (iframe && iframe.offsetParent !== null) return iframe;
				}

				return null;
			}

			function isScrollable(element) {
				if (!element) return false;

				return element.scrollHeight > element.clientHeight + 10;
			}

			function getScrollTop(element) {
				if (!element) return 0;

				return element.scrollTop || 0;
			}

			function getScrollDistance(element) {
				if (!element) return 0;

				return Math.max(0, element.scrollHeight - element.clientHeight);
			}

			function isElementVisible(element) {
				if (!element || !element.ownerDocument || !element.ownerDocument.defaultView) return false;

				const style = element.ownerDocument.defaultView.getComputedStyle(element);

				return style.display !== 'none' &&
					style.visibility !== 'hidden' &&
					element.getClientRects().length > 0;
			}

			function hasOpenModal(doc) {
				if (!doc || !doc.querySelectorAll) return false;

				if (doc.body && doc.body.classList && doc.body.classList.contains('modal-open')) {
					return true;
				}

				return modalSelectors.some(selector => {
					const elements = doc.querySelectorAll(selector);

					for (let i = 0; i < elements.length; i += 1) {
						if (isElementVisible(elements[i])) return true;
					}

					return false;
				});
			}

			function isIgnoredScrollTarget(element) {
				if (!element || !element.matches) return false;

				return ignoredScrollTargetSelectors.some(selector =>
					element.matches(selector) || !!element.closest(selector)
				);
			}

			function addCandidate(candidates, element) {
				if (!element ||
					isIgnoredScrollTarget(element) ||
					candidates.indexOf(element) !== -1) {
					return;
				}

				candidates.push(element);
			}

			function addKnownCandidates(doc, candidates) {
				addCandidate(candidates, getScrollElement(doc));
				addCandidate(candidates, doc.body);
				addCandidate(candidates, doc.documentElement);

				scrollableSelectors.forEach(selector => {
					const elements = doc.querySelectorAll(selector);

					for (let i = 0; i < elements.length; i += 1) {
						addCandidate(candidates, elements[i]);
					}
				});
			}

			function addDiscoveredCandidates(doc, candidates) {
				const now = Date.now();

				if (discoveredDocument === doc && now - lastDiscovery < discoveryInterval) {
					discoveredCandidates.forEach(candidate => addCandidate(candidates, candidate));

					return;
				}

				const elements = doc.querySelectorAll('*');

				discoveredDocument = doc;
				discoveredCandidates = [];
				lastDiscovery = now;

				for (let i = 0; i < elements.length; i += 1) {
					const scrollableElement = elements[i];

					if (!isScrollable(scrollableElement)) continue;

					const style = doc.defaultView && doc.defaultView.getComputedStyle(scrollableElement);

					if (!style) continue;

					const overflow = `${style.overflow} ${style.overflowY}`;

					if (overflow.indexOf('auto') === -1 &&
						overflow.indexOf('scroll') === -1 &&
						overflow.indexOf('overlay') === -1 &&
						getScrollTop(scrollableElement) === 0) {
						continue;
					}

					addCandidate(discoveredCandidates, scrollableElement);
					addCandidate(candidates, scrollableElement);
				}
			}

			function getBestCandidate(candidates) {
				let bestCandidate = null;
				let bestScore = -1;

				for (let i = 0; i < candidates.length; i += 1) {
					const candidate = candidates[i];

					if (!candidate ||
						isIgnoredScrollTarget(candidate) ||
						(!isScrollable(candidate) && getScrollTop(candidate) === 0)) {
						continue;
					}

					const score = (getScrollTop(candidate) * 100000) + getScrollDistance(candidate);

					if (score > bestScore) {
						bestCandidate = candidate;
						bestScore = score;
					}
				}

				return bestCandidate;
			}

			function getIframeDocument(iframe) {
				try {
					return iframe && (iframe.contentDocument || iframe.contentWindow.document);
				} catch (e) {
					return null;
				}
			}

			function getBestScrollTarget(doc) {
				const candidates = [];

				addKnownCandidates(doc, candidates);

				const knownCandidate = getBestCandidate(candidates);

				if (knownCandidate && getScrollTop(knownCandidate) > scrollThreshold) return knownCandidate;

				addDiscoveredCandidates(doc, candidates);

				const discoveredCandidate = getBestCandidate(candidates);

				if (discoveredCandidate) return discoveredCandidate;

				return getScrollElement(doc);
			}

			function getCurrentScrollTarget() {
				const iframe = getActiveIframe();
				const iframeDocument = getIframeDocument(iframe);

				if (iframeDocument) return getBestScrollTarget(iframeDocument);

				return getBestScrollTarget(document);
			}

			function setVisible(isVisible) {
				if (scope.isVisible === isVisible) return;

				scope.$evalAsync(() => {
					scope.isVisible = isVisible;
				});
			}

			function updateVisibility() {
				const iframe = getActiveIframe();
				const iframeDocument = getIframeDocument(iframe);

				if (hasOpenModal(document) || hasOpenModal(iframeDocument)) {
					setVisible(false);

					scope.$evalAsync(() => {
						scope.isTooltipVisible = false;
					});

					return;
				}

				const target = getCurrentScrollTarget();

				attachScrollListener(target);
				setVisible(!!target && getScrollTop(target) > scrollThreshold);
			}

			function requestUpdate() {
				if (raf) return;

				raf = $window.requestAnimationFrame(() => {
					raf = null;
					updateVisibility();
				});
			}

			function detachScrollListener() {
				if (detachCurrentTarget) detachCurrentTarget();

				currentTarget = null;
				detachCurrentTarget = null;
			}

			function attachScrollListener(target) {
				if (!target || currentTarget === target) return;

				detachScrollListener();

				currentTarget = target;
				currentTarget.addEventListener('scroll', requestUpdate, { passive: true });
				detachCurrentTarget = () => {
					currentTarget.removeEventListener('scroll', requestUpdate);
				};
			}

			function scrollTargetToTop(target) {
				if (!target) return;

				if (target.scrollTo) {
					target.scrollTo({
						top: 0,
						behavior: 'smooth'
					});
				} else {
					target.scrollTop = 0;
				}

				const win = target.ownerDocument && target.ownerDocument.defaultView;

				if (win && target === getScrollElement(target.ownerDocument)) {
					win.scrollTo({
						top: 0,
						behavior: 'smooth'
					});
				}
			}

			scope.isVisible = false;
			scope.isTooltipVisible = false;

			scope.showTooltip = () => {
				if (suppressTooltip) return;

				scope.isTooltipVisible = true;
			};

			scope.hideTooltip = () => {
				suppressTooltip = false;
				scope.isTooltipVisible = false;
			};

			scope.scrollToTop = () => {
				scrollTargetToTop(getCurrentScrollTarget());
				suppressTooltip = true;
				scope.isTooltipVisible = false;
				buttonElement.blur();
				requestUpdate();
			};

			$window.addEventListener('scroll', requestUpdate, { passive: true });

			checkInterval = $interval(updateVisibility, 300, 0, false);

			scope.$on('$destroy', () => {
				if (raf) $window.cancelAnimationFrame(raf);
				if (checkInterval) $interval.cancel(checkInterval);

				detachScrollListener();
				$window.removeEventListener('scroll', requestUpdate);

				if (buttonElement.parentNode) {
					buttonElement.parentNode.removeChild(buttonElement);
				}
			});
		}
	})
]);
